import type { UseToastOptions } from '@chakra-ui/react';
import { startCase } from 'lodash';
import { useEffect, useRef } from 'react';

import type { PrunableSelectionListField } from '../../data-builder.store';
import {
  selectLastPruneEvent,
  useDataBuilderSelector,
} from '../../data-builder.store';

// biome-ignore lint/suspicious/noConfusingVoidType: migrated from develop
type ToastFn = (options?: UseToastOptions) => string | number | void;

const getSelectionToastCopy = (
  selectionListField: PrunableSelectionListField,
  uoaSelectionsCleared: boolean
) => {
  const label = startCase(selectionListField);
  if (uoaSelectionsCleared) {
    return {
      title: `${label} selections cleared`,
      description: `${label} filters, custom columns, and unit of analysis selections were removed because the selected taxonomy changed.`,
    };
  }
  return {
    title: `${label} filters and columns cleared`,
    description: `${label} filters and custom columns were removed because the selected taxonomy changed.`,
  };
};

export function usePruneEventToast(toast: ToastFn) {
  const lastPruneEvent = useDataBuilderSelector(selectLastPruneEvent);
  const lastHandledTimestampRef = useRef<number | null>(null);

  useEffect(() => {
    if (lastPruneEvent === null) {
      return;
    }
    if (lastHandledTimestampRef.current === lastPruneEvent.timestamp) {
      return;
    }
    lastHandledTimestampRef.current = lastPruneEvent.timestamp;

    const toastCopy = getSelectionToastCopy(
      lastPruneEvent.field,
      lastPruneEvent.uoaSelectionsCleared
    );

    toast({
      title: toastCopy.title,
      description: toastCopy.description,
      status: 'info',
      duration: 5000,
      isClosable: true,
      position: 'top-right',
    });
  }, [lastPruneEvent, toast]);
}
